import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Leaderboard as LeaderboardIcon,
  PersonAddAlt1 as AddBirdIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import { useLicense } from '../context/LicenseContext';
import { getBirdListLabels } from '../utils/birdSearch';
import { TIER_DEFAULTS, tierText, formatCoefficient, resultTierCells } from '../utils/raceFormat';
import TierPlacingFields, { emptyTierForm, resultToTierForm } from '../components/TierPlacingFields';

const currentSeason = () => String(new Date().getFullYear());

// Compact cell line for one tier: "1/250" when both numbers exist, otherwise
// the profile card's longer wording ("1st" / "of 250").
const cellTierValue = (pos, birds) => {
  const hasPos = pos !== null && pos !== undefined && pos !== '';
  const hasBirds = birds !== null && birds !== undefined && birds !== '';
  if (hasPos && hasBirds) return `${pos}/${birds}`;
  return tierText(pos, birds);
};

const cellTierLines = (r) => resultTierCells(r, cellTierValue);

const dialogActionsSx = {
  px: { xs: 2, sm: 3 },
  pb: { xs: 2, sm: 2 },
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  gap: { xs: 1, sm: 0 },
  '& > :not(style) ~ :not(style)': {
    ml: { xs: 0, sm: 1 }
  }
};

const primaryDialogButtonSx = {
  order: { xs: 1, sm: 0 },
  width: { xs: '100%', sm: 'auto' }
};

const secondaryDialogButtonSx = {
  order: { xs: 2, sm: 0 },
  width: { xs: '100%', sm: 'auto' }
};

const destructiveDialogButtonSx = {
  order: { xs: 3, sm: 0 },
  width: { xs: '100%', sm: 'auto' },
  mr: { xs: 0, sm: 'auto' }
};

// The cell-placings dialog owns its form state so typing in it re-renders
// only the dialog, never the whole grid behind it. Mounted fresh per open
// (the parent conditionally renders it), so useState re-initializes.
const CellDialog = ({ birdLabel, race, hasResult, initialForm, saving, error, fullScreen, onSave, onClear, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const onField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 1 }}>
        {birdLabel}{' — '}{race.race || 'Unnamed race'}
        {race.race_date && (
          <Typography variant="body2" color="text.secondary">{race.race_date}</Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Divider sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Placings — fill in the levels that apply
          </Typography>
        </Divider>
        <Box display="flex" flexDirection="column" gap={2}>
          <TierPlacingFields form={form} onField={onField} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Saved to the chart and to the bird's profile as one result.
        </Typography>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        {hasResult && (
          <Button color="error" onClick={onClear} disabled={saving} sx={destructiveDialogButtonSx}>
            Delete result
          </Button>
        )}
        <Button onClick={onClose} disabled={saving} sx={secondaryDialogButtonSx}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={primaryDialogButtonSx}>
          {saving ? 'Saving…' : 'Save result'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SeasonPerformance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { state: licenseState } = useLicense();
  const readOnly = licenseState?.mode === 'readonly' || licenseState?.mode === 'unlicensed';

  const [charts, setCharts] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [season, setSeason] = useState('');
  const [chartId, setChartId] = useState(null);
  const [detail, setDetail] = useState({ races: [], birds: [], results: [] });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [allBirds, setAllBirds] = useState([]);

  // Dialogs. chartDialog covers create AND edit (editing = chart object).
  const [chartDialog, setChartDialog] = useState(null); // { editing, name, season }
  const [raceDialog, setRaceDialog] = useState(null);   // { editing, race, race_date }
  const [birdDialog, setBirdDialog] = useState(null);   // { bird }
  const [cellDialog, setCellDialog] = useState(null);   // { chartBird, race, result, form }
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [chartMenuAnchor, setChartMenuAnchor] = useState(null);
  // Coefficient standings ranked by lowest total (FCI-style) or lowest average
  // (fairer while the season is still running). Persist neither — a view choice.
  const [rankMetric, setRankMetric] = useState('total');

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return undefined;

    const previousContent = viewport.getAttribute('content');
    viewport.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=0.25');

    return () => {
      viewport.setAttribute('content', previousContent || 'width=device-width, initial-scale=1');
    };
  }, []);

  const seasons = useMemo(() => {
    const unique = [...new Set(charts.map((c) => c.season).filter(Boolean))];
    return unique.sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [charts]);

  const seasonCharts = useMemo(
    () => charts.filter((c) => c.season === season),
    [charts, season]
  );

  const activeChart = seasonCharts.find((c) => c.id === chartId) || seasonCharts[0] || null;

  const loadCharts = useCallback(async () => {
    try {
      const rows = await DatabaseService.getCharts();
      setCharts(rows);
      setLoadError('');
      return rows;
    } catch (e) {
      console.error('Failed to load season charts:', e);
      setLoadError('Could not load your season charts. Please try again.');
      return [];
    } finally {
      setLoadingCharts(false);
    }
  }, []);

  useEffect(() => { loadCharts(); }, [loadCharts]);

  // Pick a sensible season once charts arrive: this year's if it exists,
  // otherwise the most recent one on record.
  useEffect(() => {
    if (loadingCharts) return;
    if (season && seasons.includes(season)) return;
    setSeason(seasons.includes(currentSeason()) ? currentSeason() : (seasons[0] || ''));
  }, [loadingCharts, seasons, season]);

  // Keep the chart selection valid within the current season.
  useEffect(() => {
    if (seasonCharts.length === 0) { setChartId(null); return; }
    if (!seasonCharts.some((c) => c.id === chartId)) setChartId(seasonCharts[0].id);
  }, [seasonCharts, chartId]);

  // Keyed on the chart ID (not the chart object — loadCharts() rebuilds row
  // identities) and guarded by a request token so a slow response for a
  // previously selected chart can never overwrite the grid of the current
  // one (results would otherwise save into the wrong chart's columns).
  const activeChartId = activeChart ? activeChart.id : null;
  const detailRequest = useRef(0);
  const loadDetail = useCallback(async () => {
    const token = ++detailRequest.current;
    if (!activeChartId) { setDetail({ races: [], birds: [], results: [] }); return; }
    setLoadingDetail(true);
    try {
      const loaded = await DatabaseService.getChartDetail(activeChartId);
      if (detailRequest.current === token) setDetail(loaded);
    } catch (e) {
      console.error('Failed to load chart detail:', e);
      if (detailRequest.current === token) setLoadError('Could not load this chart. Please try again.');
    } finally {
      if (detailRequest.current === token) setLoadingDetail(false);
    }
  }, [activeChartId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // The bird list only feeds the Add-pigeon dialog — load it on first open,
  // not on page mount.
  const [birdsLoaded, setBirdsLoaded] = useState(false);
  useEffect(() => {
    if (!birdDialog || birdsLoaded) return;
    DatabaseService.getAllBirds()
      .then((rows) => { setAllBirds(rows); setBirdsLoaded(true); })
      .catch((e) => console.error('Failed to load birds:', e));
  }, [birdDialog, birdsLoaded]);

  // Cell lookup: one result per (pigeon, race column).
  const resultByCell = useMemo(() => {
    const map = new Map();
    for (const r of detail.results) map.set(`${r.bird_id}:${r.chart_race_id}`, r);
    return map;
  }, [detail.results]);

  // Tier labels for a fresh cell: reuse the labels already used on this
  // chart (one chart = one organisation = one terminology), else defaults.
  const chartTierLabels = useMemo(() => {
    const withLabels = detail.results.find((r) => r.t1_label || r.t2_label || r.t3_label);
    if (!withLabels) return TIER_DEFAULTS;
    return [
      withLabels.t1_label || TIER_DEFAULTS[0],
      withLabels.t2_label || TIER_DEFAULTS[1],
      withLabels.t3_label || TIER_DEFAULTS[2]
    ];
  }, [detail.results]);

  const birdsNotOnChart = useMemo(() => {
    const onChart = new Set(detail.birds.map((cb) => cb.bird_id));
    return allBirds.filter((b) => !onChart.has(b.id));
  }, [allBirds, detail.birds]);

  // Coefficient standings, kept per level (Club/Fed/Combine) since the levels
  // have very different field sizes and shouldn't be mixed. For each level we
  // collect every scored coefficient a pigeon earned this season, then total
  // and average them; the level tables that have no coefficients are dropped.
  const standings = useMemo(() => {
    const rows = detail.birds.map((cb) => {
      const levels = [[], [], []];
      for (const cr of detail.races) {
        const r = resultByCell.get(`${cb.bird_id}:${cr.id}`);
        if (!r) continue;
        for (const cell of resultTierCells(r, cellTierValue)) {
          if (cell.coeffValue !== null) levels[cell.level].push(cell.coeffValue);
        }
      }
      return { cb, levels };
    });
    return [0, 1, 2]
      .map((li) => {
        const ranked = rows
          .map(({ cb, levels }) => {
            const coeffs = levels[li];
            if (coeffs.length === 0) return null;
            const total = coeffs.reduce((a, b) => a + b, 0);
            return { cb, count: coeffs.length, total, avg: total / coeffs.length };
          })
          .filter(Boolean);
        return { levelIndex: li, label: chartTierLabels[li], ranked };
      })
      .filter((l) => l.ranked.length > 0);
  }, [detail.birds, detail.races, resultByCell, chartTierLabels]);

  const sortedStandings = useMemo(
    () =>
      standings.map((level) => ({
        ...level,
        ranked: level.ranked
          .slice()
          .sort((a, b) => a[rankMetric] - b[rankMetric] || a.avg - b.avg || a.total - b.total)
      })),
    [standings, rankMetric]
  );

  const failMessage = (e, fallback) => (e && e.isLicenseError ? e.message : fallback);

  // ---- chart create/edit/delete ----

  const saveChart = async () => {
    if (!chartDialog.name.trim()) { setDialogError('Please enter a chart name — usually the club or organisation.'); return; }
    if (!chartDialog.season.trim()) { setDialogError('Please enter a season, e.g. 2026.'); return; }
    setSaving(true);
    setDialogError('');
    try {
      let selectId;
      if (chartDialog.editing) {
        await DatabaseService.updateChart(chartDialog.editing.id, chartDialog);
        selectId = chartDialog.editing.id;
      } else {
        selectId = await DatabaseService.createChart(chartDialog);
      }
      const rows = await loadCharts();
      const saved = rows.find((c) => c.id === selectId);
      if (saved) { setSeason(saved.season || ''); setChartId(saved.id); }
      setChartDialog(null);
    } catch (e) {
      setDialogError(failMessage(e, 'Could not save the chart. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const removeChart = async () => {
    if (!activeChart) return;
    setChartMenuAnchor(null);
    if (!window.confirm(
      `Delete "${activeChart.name}"? The chart's race list and pigeon rows are removed. ` +
      'Results already recorded stay on each bird\'s profile.'
    )) return;
    try {
      await DatabaseService.deleteChart(activeChart.id);
      setChartId(null);
      await loadCharts();
    } catch (e) {
      setLoadError(failMessage(e, 'Could not delete the chart. Please try again.'));
    }
  };

  // ---- race columns ----

  const saveRace = async () => {
    if (!raceDialog.race.trim()) { setDialogError('Please enter a race name.'); return; }
    setSaving(true);
    setDialogError('');
    try {
      if (raceDialog.editing) await DatabaseService.updateChartRace(raceDialog.editing.id, raceDialog);
      else await DatabaseService.addChartRace(activeChart.id, raceDialog);
      setRaceDialog(null);
      await loadDetail();
    } catch (e) {
      setDialogError(failMessage(e, 'Could not save the race. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const removeRace = async (cr) => {
    if (!window.confirm(
      `Remove "${cr.race}" from this chart? Results already recorded for it stay on each bird's profile.`
    )) return;
    try {
      await DatabaseService.deleteChartRace(cr.id);
      await loadDetail();
    } catch (e) {
      setLoadError(failMessage(e, 'Could not remove the race. Please try again.'));
    }
  };

  // ---- pigeon rows ----

  const saveBird = async () => {
    if (!birdDialog.bird) { setDialogError('Please choose a pigeon.'); return; }
    setSaving(true);
    setDialogError('');
    try {
      await DatabaseService.addChartBird(activeChart.id, birdDialog.bird.id);
      setBirdDialog(null);
      await loadDetail();
    } catch (e) {
      setDialogError(failMessage(e, 'Could not add the pigeon. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const removeBird = async (cb) => {
    const { displayName: name } = getBirdListLabels({ name: cb.bird_name, ring_number: cb.bird_ring });
    if (!window.confirm(
      `Remove ${name} from this chart? Its recorded results stay on its profile ` +
      '(and reappear here if you add it back).'
    )) return;
    try {
      await DatabaseService.removeChartBird(cb.id);
      await loadDetail();
    } catch (e) {
      setLoadError(failMessage(e, 'Could not remove the pigeon. Please try again.'));
    }
  };

  // ---- cells ----

  const openCell = (cb, cr) => {
    if (readOnly) return;
    const result = resultByCell.get(`${cb.bird_id}:${cr.id}`) || null;
    const form = result ? resultToTierForm(result) : emptyTierForm(chartTierLabels);
    setDialogError('');
    setCellDialog({ chartBird: cb, race: cr, result, form });
  };

  const saveCell = async (form) => {
    const { chartBird, race, result } = cellDialog;
    setSaving(true);
    setDialogError('');
    try {
      // The race name/date always come from the column, so the result on the
      // bird's profile reads exactly like its chart cell. The chart link is
      // set at creation only — updates never move a result between columns.
      const payload = { ...form, race: race.race, race_date: race.race_date };
      if (result) await DatabaseService.updateRaceResult(result.id, payload);
      else await DatabaseService.addRaceResult(chartBird.bird_id, { ...payload, chart_race_id: race.id });
      setCellDialog(null);
      await loadDetail();
    } catch (e) {
      setDialogError(failMessage(e, 'Could not save the result. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const clearCell = async () => {
    const { result } = cellDialog;
    if (!result) return;
    if (!window.confirm('Delete this result? It is removed from the chart and the bird\'s profile.')) return;
    setSaving(true);
    setDialogError('');
    try {
      await DatabaseService.deleteRaceResult(result.id);
      setCellDialog(null);
      await loadDetail();
    } catch (e) {
      setDialogError(failMessage(e, 'Could not delete the result. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // ---- render ----

  const stickyColSx = {
    position: 'sticky',
    left: 0,
    backgroundColor: 'background.paper',
    borderRight: '1px solid',
    borderColor: 'divider',
    minWidth: { xs: 124, sm: 190 },
    maxWidth: { xs: 148, sm: 240 },
    width: { xs: 124, sm: 190 },
    px: { xs: 0.75, sm: 2 },
    py: { xs: 0.75, sm: 1 }
  };

  const touchIconButtonSx = {
    width: 44,
    height: 44,
    p: 1,
    flexShrink: 0
  };

  const mobileResultRowSx = {
    width: '100%',
    minHeight: 64,
    justifyContent: 'stretch',
    textAlign: 'left',
    px: 1.25,
    py: 1,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    color: 'text.primary',
    backgroundColor: 'background.paper',
    '&:hover': {
      borderColor: 'primary.light',
      backgroundColor: 'action.hover'
    }
  };

  const openNewChart = () =>
    setChartDialog({ editing: null, name: '', season: season || currentSeason() });

  if (loadingCharts) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={1.5}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <LeaderboardIcon color="primary" />
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontSize: { xs: '1.35rem', sm: '1.7rem' }, lineHeight: 1.2 }}
          >
            Season Performance
          </Typography>
        </Box>
        {charts.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <FormControl size="small" sx={{ minWidth: 140, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
              <InputLabel id="season-select-label">Season</InputLabel>
              <Select
                labelId="season-select-label"
                label="Season"
                value={seasons.includes(season) ? season : ''}
                onChange={(e) => { setSeason(e.target.value); setChartId(null); }}
              >
                {seasons.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            {!readOnly && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                sx={{ minHeight: { xs: 44, sm: 32 }, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                onClick={openNewChart}
              >
                New chart
              </Button>
            )}
          </Box>
        )}
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}

      {charts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <LeaderboardIcon color="disabled" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h6" gutterBottom>Track your season, race by race</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
              A season chart lists the races you fly with one organisation, with a row for each
              pigeon — fill in the positions week by week. Everything you enter also appears under
              Race Results on each bird's profile. Racing with more than one club? Add a chart for
              each, and keep every season separate.
            </Typography>
            {readOnly ? (
              <Typography variant="body2" color="text.secondary">
                Your license is inactive — season charts can be created once it's active again.
              </Typography>
            ) : (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewChart}>
                Create your first chart
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} minWidth={0}>
            <Tabs
              value={activeChart ? activeChart.id : false}
              onChange={(e, v) => setChartId(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: { xs: 44, sm: 40 },
                flexGrow: 1,
                minWidth: 0,
                '& .MuiTab-root': { minHeight: { xs: 44, sm: 40 } }
              }}
            >
              {seasonCharts.map((c) => <Tab key={c.id} value={c.id} label={c.name || 'Unnamed chart'} />)}
            </Tabs>
            {activeChart && !readOnly && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddBirdIcon />}
                  sx={{ minHeight: { xs: 44, sm: 32 }, flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={() => { setDialogError(''); setBirdDialog({ bird: null }); }}
                >
                  Add pigeon
                </Button>
                <IconButton
                  size="small"
                  sx={{ width: { xs: 44, sm: 34 }, height: { xs: 44, sm: 34 } }}
                  onClick={(e) => setChartMenuAnchor(e.currentTarget)}
                  aria-label="Chart options"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu anchorEl={chartMenuAnchor} open={Boolean(chartMenuAnchor)} onClose={() => setChartMenuAnchor(null)}>
                  <MenuItem onClick={() => {
                    setChartMenuAnchor(null);
                    setDialogError('');
                    setChartDialog({ editing: activeChart, name: activeChart.name || '', season: activeChart.season || '' });
                  }}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename / move season
                  </MenuItem>
                  <MenuItem onClick={removeChart} sx={{ color: 'error.main' }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete chart
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {!activeChart ? null : loadingDetail ? (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress size={28} /></Box>
          ) : (
            <>
              {false ? (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {!readOnly && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      sx={{ minHeight: 44 }}
                      onClick={() => { setDialogError(''); setRaceDialog({ editing: null, race: '', race_date: '' }); }}
                    >
                      Add race
                    </Button>
                  )}

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="overline" color="text.secondary">
                      Races
                    </Typography>
                    {detail.races.length === 0 ? (
                      <Box
                        sx={{
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 2,
                          px: 2,
                          py: 2,
                          backgroundColor: 'background.paper'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No races added to this chart yet.
                        </Typography>
                      </Box>
                    ) : (
                      detail.races.map((cr) => (
                        <Box
                          key={cr.id}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 1.25,
                            py: 1,
                            backgroundColor: 'background.paper'
                          }}
                        >
                          <Box minWidth={0} flexGrow={1}>
                            <Typography variant="subtitle2" sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                              {cr.race || 'Unnamed race'}
                            </Typography>
                            {cr.race_date && (
                              <Typography variant="caption" color="text.secondary">{cr.race_date}</Typography>
                            )}
                          </Box>
                          {!readOnly && (
                            <>
                              <Tooltip title="Edit race">
                                <IconButton
                                  sx={touchIconButtonSx}
                                  onClick={() => { setDialogError(''); setRaceDialog({ editing: cr, race: cr.race || '', race_date: cr.race_date || '' }); }}
                                  aria-label={`Edit ${cr.race || 'race'}`}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove race">
                                <IconButton
                                  sx={touchIconButtonSx}
                                  color="error"
                                  onClick={() => removeRace(cr)}
                                  aria-label={`Remove ${cr.race || 'race'}`}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>

                  {detail.birds.length === 0 ? (
                    <Box
                      sx={{
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 2,
                        py: 2.5,
                        backgroundColor: 'background.paper',
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No pigeons on this chart yet
                        {readOnly ? '.' : ' â€” add the birds you\'re racing this season below.'}
                      </Typography>
                    </Box>
                  ) : (
                    detail.birds.map((cb) => {
                      const recorded = detail.races.filter((cr) => resultByCell.has(`${cb.bird_id}:${cr.id}`)).length;
                      const { displayName, ringLabel } = getBirdListLabels({ name: cb.bird_name, ring_number: cb.bird_ring });
                      return (
                        <Card key={cb.id}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                              <Box minWidth={0} flexGrow={1}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                                  {displayName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ringLabel ? `${ringLabel} Â· ` : ''}
                                  {recorded}/{detail.races.length || 0} recorded
                                </Typography>
                              </Box>
                              {!readOnly && (
                                <Tooltip title="Remove from chart">
                                  <IconButton sx={touchIconButtonSx} onClick={() => removeBird(cb)} aria-label="Remove pigeon from chart">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1} mt={1.5}>
                              {detail.races.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  Add races above, then record results here.
                                </Typography>
                              ) : (
                                detail.races.map((cr) => {
                                  const result = resultByCell.get(`${cb.bird_id}:${cr.id}`);
                                  const lines = result ? cellTierLines(result) : [];
                                  const rowContent = (
                                    <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                      <Box minWidth={0} flexGrow={1}>
                                        <Typography variant="body2" fontWeight={650} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                                          {cr.race || 'Unnamed race'}
                                        </Typography>
                                        {cr.race_date && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {cr.race_date}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box textAlign="right" flexShrink={0}>
                                        {lines.length > 0 ? (
                                          lines.map((l, i) => (
                                            <Typography key={i} variant="caption" display="block" sx={{ lineHeight: 1.35 }}>
                                              {l.label ? `${l.label}: ` : ''}<b>{l.text}</b>{l.coeff ? <Box component="span" sx={{ color: 'text.secondary' }}> · {l.coeff}</Box> : null}
                                            </Typography>
                                          ))
                                        ) : (
                                          <Typography variant="caption" color="text.secondary">
                                            {readOnly ? 'No result' : 'Add result'}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  );

                                  return readOnly ? (
                                    <Box key={cr.id} sx={{ ...mobileResultRowSx, '&:hover': {} }}>
                                      {rowContent}
                                    </Box>
                                  ) : (
                                    <Button
                                      key={cr.id}
                                      fullWidth
                                      sx={mobileResultRowSx}
                                      onClick={() => openCell(cb, cr)}
                                      aria-label={`${result ? 'Edit' : 'Add'} result for ${displayName} in ${cr.race || 'race'}`}
                                    >
                                      {rowContent}
                                    </Button>
                                  );
                                })
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </Box>
              ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  maxHeight: { xs: 'calc(100dvh - 250px)', sm: '68vh' },
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  touchAction: 'pan-x pan-y pinch-zoom'
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    minWidth: { xs: 420, sm: 480 },
                    width: { xs: 'max-content', sm: '100%' },
                    '& .MuiTableCell-root': {
                      px: { xs: 0.75, sm: 2 },
                      py: { xs: 0.65, sm: 1 }
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...stickyColSx, zIndex: 4 }}>Pigeon</TableCell>
                      {detail.races.map((cr) => (
                        <TableCell key={cr.id} align="center" sx={{ minWidth: { xs: 96, sm: 130 }, maxWidth: { xs: 112, sm: 160 } }}>
                          <Box display="flex" alignItems="flex-start" justifyContent="center" gap={0.25}>
                            <Box minWidth={0}>
                              <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.72rem', sm: '0.875rem' }, lineHeight: 1.2, wordBreak: 'break-word' }}>
                                {cr.race || 'Unnamed race'}
                              </Typography>
                              {cr.race_date && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{cr.race_date}</Typography>
                              )}
                            </Box>
                            {!readOnly && (
                              <Box display="flex" flexDirection="column" mt={-0.5}>
                                <Tooltip title="Edit race">
                                  <IconButton
                                    size="small"
                                    sx={{ width: { xs: 30, sm: 24 }, height: { xs: 30, sm: 24 }, p: { xs: 0.5, sm: 0.25 } }}
                                    onClick={() => { setDialogError(''); setRaceDialog({ editing: cr, race: cr.race || '', race_date: cr.race_date || '' }); }}
                                    aria-label={`Edit ${cr.race || 'race'}`}
                                  >
                                    <EditIcon sx={{ fontSize: { xs: 16, sm: 14 } }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove race">
                                  <IconButton
                                    size="small"
                                    sx={{ width: { xs: 30, sm: 24 }, height: { xs: 30, sm: 24 }, p: { xs: 0.5, sm: 0.25 } }}
                                    color="error"
                                    onClick={() => removeRace(cr)}
                                    aria-label={`Remove ${cr.race || 'race'}`}
                                  >
                                    <DeleteIcon sx={{ fontSize: { xs: 16, sm: 14 } }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                      {!readOnly && (
                        <TableCell align="center" sx={{ minWidth: { xs: 78, sm: 110 } }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => { setDialogError(''); setRaceDialog({ editing: null, race: '', race_date: '' }); }}
                          >
                            Race
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.birds.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={detail.races.length + (readOnly ? 1 : 2)}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            No pigeons on this chart yet
                            {readOnly ? '.' : ' — add the birds you\'re racing this season below.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      detail.birds.map((cb) => {
                        const recorded = detail.races.filter((cr) => resultByCell.has(`${cb.bird_id}:${cr.id}`)).length;
                        const { displayName, ringLabel } = getBirdListLabels({ name: cb.bird_name, ring_number: cb.bird_ring });
                        return (
                          <TableRow key={cb.id} hover>
                            <TableCell sx={{ ...stickyColSx, zIndex: 1 }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                <Box minWidth={0}>
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.76rem', sm: '0.875rem' } }}>
                                    {displayName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                    {ringLabel ? `${ringLabel} · ` : ''}
                                    {recorded}/{detail.races.length || 0} recorded
                                  </Typography>
                                </Box>
                                {!readOnly && (
                                  <Tooltip title="Remove from chart">
                                    <IconButton
                                      size="small"
                                      sx={{ width: { xs: 30, sm: 24 }, height: { xs: 30, sm: 24 }, p: { xs: 0.5, sm: 0.25 } }}
                                      onClick={() => removeBird(cb)}
                                      aria-label="Remove pigeon from chart"
                                    >
                                      <DeleteIcon sx={{ fontSize: { xs: 16, sm: 15 } }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            {detail.races.map((cr) => {
                              const result = resultByCell.get(`${cb.bird_id}:${cr.id}`);
                              const lines = result ? cellTierLines(result) : [];
                              return (
                                <TableCell
                                  key={cr.id}
                                  align="center"
                                  onClick={() => openCell(cb, cr)}
                                  sx={{
                                    cursor: readOnly ? 'default' : 'pointer',
                                    minWidth: { xs: 96, sm: 130 },
                                    '&:hover': readOnly ? {} : { backgroundColor: 'action.hover' }
                                  }}
                                >
                                  {lines.length > 0 ? (
                                    lines.map((l, i) => (
                                      <Typography key={i} variant="caption" display="block" sx={{ fontSize: { xs: '0.66rem', sm: '0.75rem' }, lineHeight: 1.35 }}>
                                        {l.label ? `${l.label}: ` : ''}<b>{l.text}</b>{l.coeff ? <Box component="span" sx={{ color: 'text.secondary' }}> · {l.coeff}</Box> : null}
                                      </Typography>
                                    ))
                                  ) : (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: '0.66rem', sm: '0.75rem' } }}>
                                      {readOnly ? '—' : '— add —'}
                                    </Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                            {!readOnly && <TableCell />}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              {sortedStandings.length > 0 && (
                <Box mt={3}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                    <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                      Coefficient standings
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={rankMetric}
                      onChange={(e, v) => v && setRankMetric(v)}
                      aria-label="Rank standings by"
                    >
                      <ToggleButton value="total" sx={{ textTransform: 'none', px: 1.5 }}>Total</ToggleButton>
                      <ToggleButton value="avg" sx={{ textTransform: 'none', px: 1.5 }}>Average</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Coefficient = position × 1000 ÷ birds basketed (capped at 5000), per FCI World
                    Best Pigeon rules — lowest wins. Only placings with a field size (birdage)
                    recorded are counted, and the birdage should be the birds sent to the race, not
                    just the published prize winners. {rankMetric === 'total'
                      ? 'Ranked by total coefficient (FCI-style), which favours birds that scored in more races.'
                      : 'Ranked by average coefficient, a fairer comparison while the season is still running.'}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {sortedStandings.map((level) => (
                      <TableContainer key={level.levelIndex} component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Box px={1.5} py={1} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2">{level.label || `Level ${level.levelIndex + 1}`}</Typography>
                        </Box>
                        <Table size="small" sx={{ minWidth: 360 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 44 }}>#</TableCell>
                              <TableCell>Pigeon</TableCell>
                              <TableCell align="center">Races</TableCell>
                              <TableCell align="right" sx={{ fontWeight: rankMetric === 'total' ? 700 : 400 }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: rankMetric === 'avg' ? 700 : 400 }}>Average</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {level.ranked.map((row, i) => {
                              const { displayName, ringLabel } = getBirdListLabels({ name: row.cb.bird_name, ring_number: row.cb.bird_ring });
                              return (
                                <TableRow key={row.cb.id} hover>
                                  <TableCell>{i + 1}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{displayName}</Typography>
                                    {ringLabel && <Typography variant="caption" color="text.secondary">{ringLabel}</Typography>}
                                  </TableCell>
                                  <TableCell align="center">{row.count}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: rankMetric === 'total' ? 700 : 400 }}>{formatCoefficient(row.total)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: rankMetric === 'avg' ? 700 : 400 }}>{formatCoefficient(row.avg)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ))}
                  </Box>
                </Box>
              )}

              {/* The Add pigeon button lives in the tabs row above, next to the
                  chart-options menu. */}
              <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' } }}
                >
                  Results entered here also appear under Race Results on each bird's profile.
                </Typography>
              </Box>
            </>
          )}
        </>
      )}

      {/* New / edit chart */}
      <Dialog open={Boolean(chartDialog)} onClose={() => !saving && setChartDialog(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>{chartDialog?.editing ? 'Edit chart' : 'New season chart'}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          {!chartDialog?.editing && (
            <DialogContentText sx={{ mb: 2 }}>
              One chart per organisation you race with — its name usually being the club,
              federation or combine.
            </DialogContentText>
          )}
          <TextField
            label="Chart name"
            value={chartDialog?.name || ''}
            onChange={(e) => setChartDialog((d) => ({ ...d, name: e.target.value }))}
            fullWidth
            required
            autoFocus
            placeholder="e.g. Northern District Club"
            inputProps={{ maxLength: 120 }}
            sx={{ mb: 2, mt: 0.5 }}
          />
          <TextField
            label="Season"
            value={chartDialog?.season || ''}
            onChange={(e) => setChartDialog((d) => ({ ...d, season: e.target.value }))}
            fullWidth
            required
            placeholder="e.g. 2026"
            inputProps={{ maxLength: 120 }}
            helperText="Charts with the same season are shown together."
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setChartDialog(null)} disabled={saving} sx={secondaryDialogButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={saveChart} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={primaryDialogButtonSx}>
            {saving ? 'Saving…' : (chartDialog?.editing ? 'Save changes' : 'Create chart')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / edit race column */}
      <Dialog open={Boolean(raceDialog)} onClose={() => !saving && setRaceDialog(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>{raceDialog?.editing ? 'Edit race' : 'Add race to chart'}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <TextField
            label="Race"
            value={raceDialog?.race || ''}
            onChange={(e) => setRaceDialog((d) => ({ ...d, race: e.target.value }))}
            fullWidth
            required
            autoFocus
            placeholder="e.g. Kimberley 400km"
            inputProps={{ maxLength: 120 }}
            sx={{ mb: 2, mt: 0.5 }}
          />
          <TextField
            label="Date (optional)"
            value={raceDialog?.race_date || ''}
            onChange={(e) => setRaceDialog((d) => ({ ...d, race_date: e.target.value }))}
            fullWidth
            placeholder="e.g. 2026-08-15"
            inputProps={{ maxLength: 120 }}
          />
          {raceDialog?.editing && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Renaming updates the results already recorded for this race on your birds' profiles.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setRaceDialog(null)} disabled={saving} sx={secondaryDialogButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={saveRace} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={primaryDialogButtonSx}>
            {saving ? 'Saving…' : (raceDialog?.editing ? 'Save changes' : 'Add race')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add pigeon */}
      <Dialog open={Boolean(birdDialog)} onClose={() => !saving && setBirdDialog(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add pigeon to chart</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Autocomplete
            options={birdsNotOnChart}
            value={birdDialog?.bird || null}
            onChange={(e, v) => setBirdDialog((d) => ({ ...d, bird: v }))}
            getOptionLabel={(b) => {
              const { displayName, ringLabel } = getBirdListLabels(b);
              return ringLabel ? `${displayName} (${ringLabel})` : displayName;
            }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label="Pigeon" placeholder="Search by name or ring number" autoFocus />
            )}
            noOptionsText={
              !birdsLoaded ? 'Loading birds…'
                : allBirds.length === 0 ? 'No birds yet — add birds first.'
                : 'All your birds are already on this chart.'
            }
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setBirdDialog(null)} disabled={saving} sx={secondaryDialogButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={saveBird} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={primaryDialogButtonSx}>
            {saving ? 'Adding…' : 'Add pigeon'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cell placings — mounted per open so the dialog's form state resets */}
      {cellDialog && (
        <CellDialog
          birdLabel={getBirdListLabels({
            name: cellDialog.chartBird.bird_name,
            ring_number: cellDialog.chartBird.bird_ring
          }).displayName}
          race={cellDialog.race}
          hasResult={Boolean(cellDialog.result)}
          initialForm={cellDialog.form}
          saving={saving}
          error={dialogError}
          fullScreen={isMobile}
          onSave={saveCell}
          onClear={clearCell}
          onClose={() => setCellDialog(null)}
        />
      )}
    </Box>
  );
};

export default SeasonPerformance;
